/**
 * Language configuration for chat translation
 * Each language has: code (ISO), name, flag emoji, and native abbreviation
 */

export interface Language {
  code: string;
  name: string;
  flag: string;
  nativeAbbr?: string; // Native language abbreviation (e.g., "සිං" for Sinhala)
}

// Available languages in the chat system with native abbreviations
export const LANGUAGES: Language[] = [
  { code: 'EN', name: 'English', flag: '🇺🇸', nativeAbbr: 'EN' },
  { code: 'FR', name: 'French', flag: '🇫🇷', nativeAbbr: 'FR' },
  { code: 'ES', name: 'Spanish', flag: '🇪🇸', nativeAbbr: 'ES' },
  { code: 'DE', name: 'German', flag: '🇩🇪', nativeAbbr: 'DE' },
  { code: 'IT', name: 'Italian', flag: '🇮🇹', nativeAbbr: 'IT' },
  { code: 'PT', name: 'Portuguese', flag: '🇵🇹', nativeAbbr: 'PT' },
  { code: 'NL', name: 'Dutch', flag: '🇳🇱', nativeAbbr: 'NL' },
  { code: 'RU', name: 'Russian', flag: '🇷🇺', nativeAbbr: 'РУ' },
  { code: 'JA', name: 'Japanese', flag: '🇯🇵', nativeAbbr: '日本' },
  { code: 'KO', name: 'Korean', flag: '🇰🇷', nativeAbbr: '한국' },
  { code: 'ZH', name: 'Chinese', flag: '🇨🇳', nativeAbbr: '中文' },
  { code: 'AR', name: 'Arabic', flag: '🇸🇦', nativeAbbr: 'عر' },
  { code: 'HI', name: 'Hindi', flag: '🇮🇳', nativeAbbr: 'हिं' },
  { code: 'TH', name: 'Thai', flag: '🇹🇭', nativeAbbr: 'ไทย' },
  { code: 'VI', name: 'Vietnamese', flag: '🇻🇳', nativeAbbr: 'VI' },
  { code: 'TR', name: 'Turkish', flag: '🇹🇷', nativeAbbr: 'TR' },
  { code: 'PL', name: 'Polish', flag: '🇵🇱', nativeAbbr: 'PL' },
  { code: 'SV', name: 'Swedish', flag: '🇸🇪', nativeAbbr: 'SV' },
  { code: 'NO', name: 'Norwegian', flag: '🇳🇴', nativeAbbr: 'NO' },
  { code: 'DA', name: 'Danish', flag: '🇩🇰', nativeAbbr: 'DA' },
  { code: 'FI', name: 'Finnish', flag: '🇫🇮', nativeAbbr: 'FI' },
  { code: 'EL', name: 'Greek', flag: '🇬🇷', nativeAbbr: 'ΕΛ' },
  { code: 'HE', name: 'Hebrew', flag: '🇮🇱', nativeAbbr: 'עב' },
  { code: 'CS', name: 'Czech', flag: '🇨🇿', nativeAbbr: 'CS' },
  { code: 'HU', name: 'Hungarian', flag: '🇭🇺', nativeAbbr: 'HU' },
  { code: 'RO', name: 'Romanian', flag: '🇷🇴', nativeAbbr: 'RO' },
  { code: 'BG', name: 'Bulgarian', flag: '🇧🇬', nativeAbbr: 'БГ' },
  { code: 'HR', name: 'Croatian', flag: '🇭🇷', nativeAbbr: 'HR' },
  { code: 'SK', name: 'Slovak', flag: '🇸🇰', nativeAbbr: 'SK' },
  { code: 'SI', name: 'Sinhala', flag: '🇱🇰', nativeAbbr: 'සිං' },
  { code: 'BN', name: 'Bengali', flag: '🇧🇩', nativeAbbr: 'বাং' },
  { code: 'UR', name: 'Urdu', flag: '🇵🇰', nativeAbbr: 'اردو' },
  { code: 'FA', name: 'Persian', flag: '🇮🇷', nativeAbbr: 'فا' },
  { code: 'SW', name: 'Swahili', flag: '🇰🇪', nativeAbbr: 'SW' },
  { code: 'AF', name: 'Afrikaans', flag: '🇿🇦', nativeAbbr: 'AF' },
  { code: 'ID', name: 'Indonesian', flag: '🇮🇩', nativeAbbr: 'ID' },
  { code: 'MS', name: 'Malay', flag: '🇲🇾', nativeAbbr: 'MS' },
  { code: 'TL', name: 'Filipino', flag: '🇵🇭', nativeAbbr: 'TL' }
];

// Country code to language code mapping
export const COUNTRY_TO_LANGUAGE: Record<string, string> = {
  // European countries
  'FR': 'FR', // France -> French
  'ES': 'ES', // Spain -> Spanish
  'DE': 'DE', // Germany -> German
  'IT': 'IT', // Italy -> Italian
  'PT': 'PT', // Portugal -> Portuguese
  'NL': 'NL', // Netherlands -> Dutch
  'PL': 'PL', // Poland -> Polish
  'RU': 'RU', // Russia -> Russian
  'TR': 'TR', // Turkey -> Turkish
  'GR': 'EL', // Greece -> Greek
  'SE': 'SV', // Sweden -> Swedish
  'NO': 'NO', // Norway -> Norwegian
  'DK': 'DA', // Denmark -> Danish
  'FI': 'FI', // Finland -> Finnish
  'IS': 'IS', // Iceland -> Icelandic
  'CZ': 'CS', // Czech Republic -> Czech
  'SK': 'SK', // Slovakia -> Slovak
  'HU': 'HU', // Hungary -> Hungarian
  'RO': 'RO', // Romania -> Romanian
  'BG': 'BG', // Bulgaria -> Bulgarian
  'HR': 'HR', // Croatia -> Croatian
  'SI': 'SL', // Slovenia -> Slovenian
  'EE': 'ET', // Estonia -> Estonian
  'LV': 'LV', // Latvia -> Latvian
  'LT': 'LT', // Lithuania -> Lithuanian

  // Asian countries
  'JP': 'JA', // Japan -> Japanese
  'KR': 'KO', // South Korea -> Korean
  'CN': 'ZH', // China -> Chinese
  'TW': 'ZH', // Taiwan -> Chinese
  'HK': 'ZH', // Hong Kong -> Chinese
  'TH': 'TH', // Thailand -> Thai
  'VN': 'VI', // Vietnam -> Vietnamese
  'ID': 'ID', // Indonesia -> Indonesian
  'MY': 'MS', // Malaysia -> Malay
  'SG': 'EN', // Singapore -> English
  'PH': 'TL', // Philippines -> Filipino
  'IN': 'HI', // India -> Hindi
  'BD': 'BN', // Bangladesh -> Bengali
  'PK': 'UR', // Pakistan -> Urdu
  'LK': 'SI', // Sri Lanka -> Sinhala
  'NP': 'NE', // Nepal -> Nepali
  'MM': 'MY', // Myanmar -> Burmese
  'KH': 'KM', // Cambodia -> Khmer
  'LA': 'LO', // Laos -> Lao
  'MN': 'MN', // Mongolia -> Mongolian
  'KZ': 'KK', // Kazakhstan -> Kazakh
  'UZ': 'UZ', // Uzbekistan -> Uzbek
  'KG': 'KY', // Kyrgyzstan -> Kyrgyz
  'TJ': 'TG', // Tajikistan -> Tajik
  'TM': 'TK', // Turkmenistan -> Turkmen
  'AF': 'PS', // Afghanistan -> Pashto
  'IR': 'FA', // Iran -> Persian
  'IQ': 'AR', // Iraq -> Arabic
  'SY': 'AR', // Syria -> Arabic
  'LB': 'AR', // Lebanon -> Arabic
  'JO': 'AR', // Jordan -> Arabic
  'SA': 'AR', // Saudi Arabia -> Arabic
  'AE': 'AR', // UAE -> Arabic
  'QA': 'AR', // Qatar -> Arabic
  'KW': 'AR', // Kuwait -> Arabic
  'BH': 'AR', // Bahrain -> Arabic
  'OM': 'AR', // Oman -> Arabic
  'YE': 'AR', // Yemen -> Arabic
  'IL': 'HE', // Israel -> Hebrew
  'AM': 'HY', // Armenia -> Armenian
  'AZ': 'AZ', // Azerbaijan -> Azerbaijani
  'GE': 'KA', // Georgia -> Georgian

  // African countries
  'EG': 'AR', // Egypt -> Arabic
  'LY': 'AR', // Libya -> Arabic
  'TN': 'AR', // Tunisia -> Arabic
  'DZ': 'AR', // Algeria -> Arabic
  'MA': 'AR', // Morocco -> Arabic
  'SD': 'AR', // Sudan -> Arabic
  'ZA': 'AF', // South Africa -> Afrikaans
  'NG': 'EN', // Nigeria -> English
  'KE': 'SW', // Kenya -> Swahili
  'TZ': 'SW', // Tanzania -> Swahili
  'UG': 'EN', // Uganda -> English
  'RW': 'RW', // Rwanda -> Kinyarwanda
  'ET': 'AM', // Ethiopia -> Amharic
  'GH': 'EN', // Ghana -> English
  'SN': 'FR', // Senegal -> French
  'ML': 'FR', // Mali -> French
  'BF': 'FR', // Burkina Faso -> French
  'NE': 'FR', // Niger -> French
  'TD': 'FR', // Chad -> French
  'CM': 'FR', // Cameroon -> French
  'CG': 'FR', // Congo -> French
  'CD': 'FR', // DR Congo -> French
  'CF': 'FR', // Central African Republic -> French
  'GA': 'FR', // Gabon -> French
  'DJ': 'FR', // Djibouti -> French
  'MG': 'MG', // Madagascar -> Malagasy

  // Americas
  'US': 'EN', // United States -> English
  'CA': 'EN', // Canada -> English
  'MX': 'ES', // Mexico -> Spanish
  'GT': 'ES', // Guatemala -> Spanish
  'BZ': 'EN', // Belize -> English
  'SV': 'ES', // El Salvador -> Spanish
  'HN': 'ES', // Honduras -> Spanish
  'NI': 'ES', // Nicaragua -> Spanish
  'CR': 'ES', // Costa Rica -> Spanish
  'PA': 'ES', // Panama -> Spanish
  'CU': 'ES', // Cuba -> Spanish
  'DO': 'ES', // Dominican Republic -> Spanish
  'HT': 'FR', // Haiti -> French
  'JM': 'EN', // Jamaica -> English
  'TT': 'EN', // Trinidad and Tobago -> English
  'BB': 'EN', // Barbados -> English
  'CO': 'ES', // Colombia -> Spanish
  'VE': 'ES', // Venezuela -> Spanish
  'GY': 'EN', // Guyana -> English
  'SR': 'NL', // Suriname -> Dutch
  'BR': 'PT', // Brazil -> Portuguese
  'AR': 'ES', // Argentina -> Spanish
  'UY': 'ES', // Uruguay -> Spanish
  'PY': 'ES', // Paraguay -> Spanish
  'BO': 'ES', // Bolivia -> Spanish
  'PE': 'ES', // Peru -> Spanish
  'EC': 'ES', // Ecuador -> Spanish
  'CL': 'ES', // Chile -> Spanish

  // Oceania
  'AU': 'EN', // Australia -> English
  'NZ': 'EN', // New Zealand -> English
  'FJ': 'EN', // Fiji -> English
  'PG': 'EN', // Papua New Guinea -> English
  'SB': 'EN', // Solomon Islands -> English
  'VU': 'FR', // Vanuatu -> French
  'NC': 'FR', // New Caledonia -> French
  'PF': 'FR', // French Polynesia -> French
  'CK': 'EN', // Cook Islands -> English
  'TO': 'EN', // Tonga -> English
  'WS': 'EN', // Samoa -> English
  'KI': 'EN', // Kiribati -> English
  'TV': 'EN', // Tuvalu -> English
  'NR': 'EN', // Nauru -> English
  'PW': 'EN', // Palau -> English
  'FM': 'EN', // Micronesia -> English
  'MH': 'EN', // Marshall Islands -> English

  // Default fallback
  'GB': 'EN' // United Kingdom -> English
};

// Helper to get default language
export const DEFAULT_LANGUAGE = LANGUAGES.find(lang => lang.code === 'EN') || LANGUAGES[0];
